import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  useToast,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Heading,
  Divider,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import SalesAgreementsApi from '../../../../api/salesAgreements';
import GovernmentDataApi from '../../../../api/governmentData';

const EditSalesAgreementModal = ({ isOpen, onClose, onSuccess, agreement }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyId: '',
    companyName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    planType: '',
    monthlyFee: '',
    pilotEnabled: false,
    pilotDays: '',
    setupFeeAmount: '',
    withdrawalFeeAmount: '9.5',
    withdrawalFeePaidBy: 'employee',
    withdrawalFeeFreeMonthly: '0',
    customConditions: [], // Array of { text: string }
  });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const toast = useToast();

  // Populate form data when agreement changes
  useEffect(() => {
    if (agreement && isOpen) {
      // Parse JSON fields
      const pilotData = agreement.pilotJson ? JSON.parse(agreement.pilotJson) : { enabled: false, days: 0 };
      const setupFeeData = agreement.setupFeeJson ? JSON.parse(agreement.setupFeeJson) : { amount: 0, isActive: false };
      const withdrawalFeeData = agreement.withdrawalFeeJson ? JSON.parse(agreement.withdrawalFeeJson) : { amount: 9.5, paidBy: 'employee', freeMonthlyCount: 0 };
      const customConditionsData = agreement.customConditionsJson ? JSON.parse(agreement.customConditionsJson) : [];

      setFormData({
        companyId: agreement.companyId || '',
        companyName: agreement.companyName || '',
        contactName: agreement.contactName || '',
        contactPhone: agreement.contactPhone || '',
        contactEmail: agreement.contactEmail || '',
        planType: agreement.planType || '',
        monthlyFee: agreement.monthlyFee ? agreement.monthlyFee.toString() : '',
        pilotEnabled: pilotData.enabled || false,
        pilotDays: pilotData.days ? pilotData.days.toString() : '',
        setupFeeAmount: setupFeeData.isActive && setupFeeData.amount ? setupFeeData.amount.toString() : '',
        withdrawalFeeAmount: withdrawalFeeData.amount ? withdrawalFeeData.amount.toString() : '9.5',
        withdrawalFeePaidBy: withdrawalFeeData.paidBy || 'employee',
        withdrawalFeeFreeMonthly: withdrawalFeeData.freeMonthlyCount ? withdrawalFeeData.freeMonthlyCount.toString() : '0',
        customConditions: Array.isArray(customConditionsData) ? customConditionsData : [],
      });
    }
  }, [agreement, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Custom conditions handlers
  const handleAddCondition = () => {
    setFormData(prev => ({
      ...prev,
      customConditions: [...prev.customConditions, { text: '' }]
    }));
  };

  const handleRemoveCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      customConditions: prev.customConditions.filter((_, i) => i !== index)
    }));
  };

  const handleConditionChange = (index, value) => {
    // Limit to 80 characters
    const limitedValue = value.substring(0, 80);
    setFormData(prev => ({
      ...prev,
      customConditions: prev.customConditions.map((condition, i) =>
        i === index ? { text: limitedValue } : condition
      )
    }));
  };

  const handleCompanyIdChange = async (companyId) => {
    handleInputChange('companyId', companyId);
    
    if (companyId.length >= 8) {
      try {
        const response = await GovernmentDataApi.getCompanyByGovId(companyId);
        if (response.success) {
          const company = response.company;
          const companyName = GovernmentDataApi.getCompanyName(company);
          handleInputChange('companyName', companyName);
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    }
  };

  const validateForm = () => {
    if (!formData.companyId) {
      toast({
        title: 'שגיאה',
        description: 'יש להזין מזהה חברה',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!formData.companyName) {
      toast({
        title: 'שגיאה',
        description: 'יש להזין שם חברה',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!formData.contactName) {
      toast({
        title: 'שגיאה',
        description: 'יש להזין שם איש קשר',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!formData.contactPhone) {
      toast({
        title: 'שגיאה',
        description: 'יש להזין טלפון איש קשר',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!formData.monthlyFee) {
      toast({
        title: 'שגיאה',
        description: 'יש להזין עלות חודשית',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data for API - let backend handle defaults
      const apiData = {
        companyId: formData.companyId,
        companyName: formData.companyName,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail || null,
        monthlyFee: parseFloat(formData.monthlyFee),
        // Optional fields - backend will apply defaults if missing
        planType: formData.planType ? formData.planType.toLowerCase() : undefined, // Normalize to lowercase, let backend default to 'basic'
        // Pilot configuration
        pilotEnabled: formData.pilotEnabled,
        pilotDays: formData.pilotEnabled ? parseInt(formData.pilotDays) || 0 : undefined,
        // Setup fee configuration
        setupFeeAmount: formData.setupFeeAmount && parseFloat(formData.setupFeeAmount) > 0
          ? parseFloat(formData.setupFeeAmount)
          : undefined,
        // Withdrawal fee configuration - provide individual fields for backend merging
        withdrawalFeeJson: JSON.stringify({
          amount: formData.withdrawalFeeAmount ? parseFloat(formData.withdrawalFeeAmount) : 9.5,
          paidBy: formData.withdrawalFeePaidBy || 'employee',
          freeMonthlyCount: formData.withdrawalFeeFreeMonthly ? parseInt(formData.withdrawalFeeFreeMonthly) : 0
        }),
        // Custom conditions - filter out empty conditions
        customConditionsJson: formData.customConditions.filter(c => c.text.trim()).length > 0
          ? JSON.stringify(formData.customConditions.filter(c => c.text.trim()))
          : null
      };

      await SalesAgreementsApi.updateSalesAgreement(agreement.id, apiData);

      toast({
        title: 'הצלחה',
        description: 'הסכם המכירות עודכן בהצלחה',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
    } catch (error) {
      console.error('Error updating sales agreement:', error);
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בעדכון הסכם המכירות',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!agreement) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="600px">
        <ModalHeader>
          <Heading size="lg" color={textColor}>
            עריכת הסכם מכירות
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Company Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor}>
              פרטי החברה
            </Text>
            <Divider />
            
            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>מזהה חברה</FormLabel>
                <Input
                  value={formData.companyId}
                  onChange={(e) => handleCompanyIdChange(e.target.value)}
                  placeholder="הכנס מזהה חברה"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>שם החברה</FormLabel>
                <Input
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="הכנס שם חברה"
                />
              </FormControl>
            </HStack>

            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>שם איש קשר</FormLabel>
                <Input
                  value={formData.contactName}
                  onChange={(e) => handleInputChange('contactName', e.target.value)}
                  placeholder="הכנס שם איש קשר"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>טלפון איש קשר</FormLabel>
                <Input
                  value={formData.contactPhone}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  placeholder="הכנס טלפון"
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>אימייל איש קשר</FormLabel>
              <Input
                value={formData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                placeholder="הכנס אימייל (אופציונלי)"
                type="email"
              />
            </FormControl>

            {/* Plan Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              פרטי התוכנית
            </Text>
            <Divider />

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>מנוי</FormLabel>
                <Select
                  value={formData.planType}
                  onChange={(e) => handleInputChange('planType', e.target.value)}
                  placeholder="בחר סוג מנוי"
                >
                  <option value="Basic">Basic</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Premium">Premium</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>ריטיינר (₪)</FormLabel>
                <NumberInput
                  value={formData.monthlyFee}
                  onChange={(value) => handleInputChange('monthlyFee', value)}
                  min={0}
                >
                  <NumberInputField placeholder="הכנס סכום" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            {/* Pilot Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              פיילוט
            </Text>
            <Divider />

            <Checkbox
              isChecked={formData.pilotEnabled}
              onChange={(e) => handleInputChange('pilotEnabled', e.target.checked)}
            >
              פיילוט
            </Checkbox>

            {formData.pilotEnabled && (
              <FormControl>
                <FormLabel>מספר ימי פיילוט</FormLabel>
                <NumberInput
                  value={formData.pilotDays}
                  onChange={(value) => handleInputChange('pilotDays', value)}
                  min={1}
                >
                  <NumberInputField placeholder="הכנס מספר ימים" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            )}

            {/* Setup Fee Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              עמלת הקמה
            </Text>
            <Divider />

            <FormControl>
              <FormLabel>סכום עמלת הקמה (₪)</FormLabel>
              <NumberInput
                value={formData.setupFeeAmount}
                onChange={(value) => handleInputChange('setupFeeAmount', value)}
                min={0}
              >
                <NumberInputField placeholder="הכנס סכום (אופציונלי)" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            {/* Withdrawal Fee Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              עמלת משיכה
            </Text>
            <Divider />

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>סכום עמלת משיכה (₪)</FormLabel>
                <NumberInput
                  value={formData.withdrawalFeeAmount}
                  onChange={(value) => handleInputChange('withdrawalFeeAmount', value)}
                  min={0}
                  step={0.1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>מי משלם</FormLabel>
                <Select
                  value={formData.withdrawalFeePaidBy}
                  onChange={(e) => handleInputChange('withdrawalFeePaidBy', e.target.value)}
                >
                  <option value="employee">עובד</option>
                  <option value="employer">מעסיק</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>משיכות חודשיות בחינם</FormLabel>
              <NumberInput
                value={formData.withdrawalFeeFreeMonthly}
                onChange={(value) => handleInputChange('withdrawalFeeFreeMonthly', value)}
                min={0}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            {/* Custom Conditions */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              תנאים מותאמים אישית
            </Text>
            <Divider />

            <VStack spacing={2} align="stretch">
              {formData.customConditions.map((condition, index) => (
                <HStack key={index} spacing={2}>
                  <Input
                    value={condition.text}
                    onChange={(e) => handleConditionChange(index, e.target.value)}
                    placeholder="הכנס תנאי (עד 80 תווים)"
                    maxLength={80}
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    colorScheme="red"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCondition(index)}
                    aria-label="הסר תנאי"
                  />
                </HStack>
              ))}
              <Button
                leftIcon={<AddIcon />}
                variant="outline"
                colorScheme="blue"
                size="sm"
                onClick={handleAddCondition}
                alignSelf="flex-start"
              >
                הוסף תנאי
              </Button>
              <Text fontSize="xs" color="gray.500">
                תנאים אלו יופיעו תחת "תנאי השירות" בעמוד התנאים המסחריים
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            ביטול
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="מעדכן..."
          >
            עדכן הסכם
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditSalesAgreementModal;
